import {
  forwardRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import StartGame from "../game/main";
import { EventBus } from "../game/EventBus";
import {
  AccountContext,
  ChallengeContext,
  WebSocketContext,
  // WebSocketContext,
} from "../App.tsx";
import { DEFAULT_ZONE_ID, EVENTS_NAME } from "../game/consts.ts";
import { IRefPhaserGame, StoryContext, ZoneContext } from "./Game.tsx";
import {
  acceptMission,
  damageMob,
  getStoryline,
  interactNpc,
  interactResource,
  playerMove,
  reportPlayerDamage,
  sendMobPositionUpdate,
} from "../api.ts";
import debounce from "debounce";
import { throttle } from "../game/helpers/throttle.ts";
import { toast } from "../components/Toasts.tsx";

interface IProps {
  currentActiveScene?: (scene_instance: Phaser.Scene) => void;
  config: Phaser.Types.Core.GameConfig;
}
export const PhaserGame = forwardRef<IRefPhaserGame, IProps>(
  function PhaserGame({ config, currentActiveScene }, ref) {
    const game = useRef<Phaser.Game | null>(null!);
    const [storyContext, setStoryContext] = useContext(StoryContext);
    const [, setZoneContext] = useContext(ZoneContext);
    // story_id of the rect the player is currently standing in (null = none).
    // Set on story-zone entry, cleared on leave; the async StorylineResponse is
    // only displayed if it still matches (player hasn't walked off in between).
    const currentStoryIdRef = useRef<number | null>(null);
    const [challengeContext, setChallengeContext] =
      useContext(ChallengeContext);
    // The zone-overlap handler below is registered once but needs the LIVE
    // challenge context (its closure would otherwise be stale). A ref keeps it
    // current without re-registering the debounced listener every render.
    const challengeContextRef = useRef(challengeContext);
    challengeContextRef.current = challengeContext;
    // Re-show cooldown: entering a challenge zone within this window of the
    // last time THAT challenge was shown is skipped. During fights you dodge
    // in and out of zones constantly — without this the dialog re-pops on
    // every re-entry. (Manual reopen via the ? button is unaffected.)
    const lastChallengeShownRef = useRef<Record<string, number>>({});
    const CHALLENGE_RESHOW_COOLDOWN_MS = 5000;
    const websocket = useContext(WebSocketContext);
    const [account] = useContext(AccountContext);

    useLayoutEffect(() => {
      if (game.current === null) {
        game.current = StartGame(config, "game-container");

        if (typeof ref === "function") {
          ref({ game: game.current, scene: null });
        } else if (ref) {
          ref.current = { game: game.current, scene: null };
        }
      }

      return () => {
        if (game.current) {
          game.current.destroy(true);
          game.current = null;
        }
      };
    }, [config, ref]);

    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.playerMove,
        // THROTTLE, not debounce: during continuous movement (held WASD key,
        // long click-to-move path) move events arrive every frame, and a
        // debounce stays silent until movement STOPS — so the server never
        // burned fuel mid-travel and the client's speed never decayed (the
        // "hold W forever at full speed" exploit). Throttling reports the
        // latest position every interval WHILE moving, plus a trailing send
        // with the final position.
        throttle(
          (position: {
            zone: string;
            x: number | string;
            y: number | string;
          }) => {
            if (websocket) {
              playerMove(
                websocket,
                DEFAULT_ZONE_ID,
                parseInt(position.x.toString()),
                parseInt(position.y.toString()),
              );
            } else {
              console.error("WebSocket is not available for player movement.");
            }
          },
          300,
        ),
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.playerMove);
      };
    }, [websocket]);

    // ── Combat monster reports → websocket ──
    // mobMove is already rate-limited inside Monster (300ms per mob), so no
    // throttle here; mobDamage/playerDamaged are naturally sparse.
    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.mobMove,
        (m: { server_iid: number; x: number; y: number }) => {
          if (websocket) {
            sendMobPositionUpdate(websocket, m.server_iid, m.x, m.y);
          }
        },
      );
      EventBus.on(
        EVENTS_NAME.mobDamage,
        (d: { server_iid: number; damage: number }) => {
          if (websocket) {
            damageMob(websocket, d.server_iid, d.damage);
          }
        },
      );
      EventBus.on(EVENTS_NAME.playerDamaged, (d: { damage: number }) => {
        if (websocket) {
          reportPlayerDamage(websocket, d.damage);
        }
      });
      return () => {
        EventBus.removeListener(EVENTS_NAME.mobMove);
        EventBus.removeListener(EVENTS_NAME.mobDamage);
        EventBus.removeListener(EVENTS_NAME.playerDamaged);
      };
    }, [websocket]);

    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.interactResource,
        debounce(
          (resource: {
            resource_id: number | string;
            resource_name: string;
          }) => {
            if (websocket) {
              interactResource(
                websocket,
                parseInt(resource.resource_id.toString()),
              );
            } else {
              console.error("WebSocket is not available for interaction.");
            }
          },
          1000,
          { immediate: true },
        ),
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.interactResource);
      };
    }, [websocket]);

    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.zoneEnter,
        debounce(
          (name: string) => {
            setZoneContext({ currentZone: name });
          },
          1000,
          { immediate: true },
        ),
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.zoneEnter);
      };
    }, [setZoneContext]);

    useEffect(() => {
      EventBus.on(EVENTS_NAME.clearGameUi, (clearString: string) => {
        switch (clearString) {
          case "zone":
            setZoneContext({ currentZone: "" });
            break;
          case "story":
            // Left the rect: forget it so a late StorylineResponse is ignored.
            currentStoryIdRef.current = null;
            setStoryContext({
              ...storyContext,
              storyDialogOpen: false,
              storyTitle: "",
              storyText: "",
            });
            break;
          case "challenge":
            setChallengeContext({
              ...challengeContext,
              challengeDialogOpen: false,
              challengeAvailable: false,
              challengeTitle: "",
              challengeText: "",
            });
            break;
          default:
            console.log("clearGameUi default case");
        }
      });
      return () => {
        EventBus.removeListener(EVENTS_NAME.clearGameUi);
      };
    }, [
      challengeContext,
      setChallengeContext,
      setStoryContext,
      setZoneContext,
      storyContext,
    ]);

    useEffect(() => {
      // Entering a story rect: the rect now carries only story_id, so ask the
      // server for the text (server-backed, like challenges). Remember which
      // rect we're in; the StorylineResponse handler shows the dialog only if
      // we're still in it.
      EventBus.on(
        EVENTS_NAME.showStoryline,
        debounce(
          (storyline: any) => {
            const storyId = parseInt(storyline.data.list.story_id);
            if (Number.isNaN(storyId)) return;
            currentStoryIdRef.current = storyId;
            if (websocket) {
              getStoryline(websocket, storyId);
            }
          },
          1000,
          { immediate: true },
        ),
      );
      // Server answered with the story text — display only if the player is
      // still standing in that same rect.
      EventBus.on(
        EVENTS_NAME.storylineResponse,
        (payload: { story_id: number; story_title: string; story: string }) => {
          if (currentStoryIdRef.current !== payload.story_id) return;
          setStoryContext({
            storyTitle: payload.story_title,
            storyText: payload.story,
            storyDialogOpen: true,
          });
        },
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.showStoryline);
        EventBus.removeListener(EVENTS_NAME.storylineResponse);
      };
    }, [setStoryContext, websocket]);

    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.showChallenge,
        debounce(
          (challenge: any) => {
            const key = `static:${challenge.data.list.challenge_title}`;
            const last = lastChallengeShownRef.current[key] || 0;
            if (Date.now() - last < CHALLENGE_RESHOW_COOLDOWN_MS) return;
            lastChallengeShownRef.current[key] = Date.now();
            setChallengeContext({
              challengeTitle: challenge.data.list.challenge_title,
              challengeText: challenge.data.list.challenge_text,
              challengeGiver: challenge.data.list.name,
              challengeDialogOpen: true,
              interactive: false,
            });
          },
          1000,
          { immediate: true },
        ),
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.showChallenge);
      };
    }, [setChallengeContext]);

    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.showInteractiveChallenge,
        debounce(
          (challenge: any) => {
            const cid = parseInt(challenge.data.list.challenge_id);
            const cur = challengeContextRef.current;
            // Already have this exact challenge's data loaded? Skip the
            // re-fetch — the debounced zone-entry handler re-fires roughly
            // every second while the player lingers in the zone, and
            // repeating interactNpc/acceptMission for the same challenge is
            // pure network chatter. This is keyed on data availability, NOT
            // on whether the panel is open: opening/closing the panel is now
            // exclusively the player's explicit click, and must never be
            // driven by zone re-entry (that was the "auto-opens while
            // running around" bug — see websocketService.ts's
            // InteractNPCResponse handler for the other half of the fix).
            if (cur.challengeAvailable && cur.challengeId === cid) return;
            // Shown recently? Skip the auto-reopen (combat weaving).
            const last = lastChallengeShownRef.current[cid] || 0;
            if (Date.now() - last < CHALLENGE_RESHOW_COOLDOWN_MS) return;
            lastChallengeShownRef.current[cid] = Date.now();
            // NOTE: there used to be an "already completed the whole
            // mission" short-circuit here keyed on objectives_complete > 0.
            // That's wrong for multi-objective missions (2026-08-02, the
            // Campaign sectors: 6 objectives sharing one bitmask) — clearing
            // objective 1 alone would trip it and silently block ever
            // reaching objectives 2-6 again. There's no reliable client-side
            // way to know "every objective is done" before asking the server
            // anyway (that's exactly what all_objectives_complete answers) —
            // so this always proceeds to interactNpc and lets the response,
            // via ChallengeDialog's challengeWon, decide what to show.
            if (websocket) {
              const giverId = parseInt(challenge.data.list.mission_giver_id);
              // Always ask the server for this NPC's mission. The response
              // (InteractNPCResponse) carries the objective's challenge_type /
              // challenge_payload — that's what drives a rich renderer, and we
              // need it again every time the challenge is opened (incl. re-entry
              // after it was already accepted).
              interactNpc(websocket, giverId);
              // Accept the mission only if a progress record doesn't already
              // exist (was previously also gated on objectives_complete===0,
              // which broke the same way for multi-objective missions —
              // accept_mission is idempotent server-side regardless, so this
              // was only ever a wasted round-trip, not a correctness risk,
              // but worth fixing while in here).
              const alreadyAccepted = account?.missions_in_progress?.some(
                (mission: any) => parseInt(mission.mission_id) === cid,
              );
              if (!alreadyAccepted) {
                acceptMission(websocket, giverId, cid);
              }
              // The server's InteractNPCResponse is the SINGLE thing that opens
              // the dialog — it carries the challenge title/text + type/payload
              // for BOTH the fresh and already-accepted cases (the server does
              // not filter accepted missions). No localStorage cache, so this is
              // correct across devices, and the dialog opens straight into the
              // rich renderer (no plain->rich flicker).
            } else {
              toast("Error", "WebSocket is not available for accepting missions.", "error");
            }
          },
          1000,
          { immediate: true },
        ),
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.showInteractiveChallenge);
      };
    }, [account, setChallengeContext, websocket]);

    useEffect(() => {
      EventBus.on(
        EVENTS_NAME.currentSceneReady,
        (scene_instance: Phaser.Scene) => {
          if (currentActiveScene && typeof currentActiveScene === "function") {
            currentActiveScene(scene_instance);
          }

          if (typeof ref === "function") {
            ref({ game: game.current, scene: scene_instance });
          } else if (ref) {
            ref.current = { game: game.current, scene: scene_instance };
          }
          EventBus.emit(EVENTS_NAME.currentSceneReadyContinued, scene_instance);
        },
      );
      return () => {
        EventBus.removeListener(EVENTS_NAME.currentSceneReady);
      };
    }, [currentActiveScene, ref]);

    return <div id="game-container"></div>;
  },
);
